import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Product } from '@/types';
import { useOrganization } from '@/hooks/useOrganization';
import { productsService } from '@/services/products';
import { toast as sonnerToast } from 'sonner';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useContext, useEffect } from 'react';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME_PRODUCTS } from '@/lib/queryCacheConfig';
import { getSupabaseClient } from '@/integrations/supabase/client';

export const useProducts = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { translator } = useContext(LanguageProviderContext);
  const orgId = currentOrganization?.id ?? '';

  const { data: products = [], isLoading, error } = useQuery({
    queryKey: queryKeys.products(orgId),
    queryFn: async () => {
      if (!currentOrganization) return [];
      return productsService.getProducts(currentOrganization.id);
    },
    enabled: !!currentOrganization,
    staleTime: STALE_TIME_PRODUCTS.products,
  });

  const { data: productsForSales = [], isLoading: isLoadingForSales } = useQuery({
    queryKey: queryKeys.productsForSales(orgId),
    queryFn: async () => {
      if (!currentOrganization) return [];
      return productsService.getProductsForSales(currentOrganization.id);
    },
    enabled: !!currentOrganization,
    staleTime: STALE_TIME_PRODUCTS.productsForSales,
  });

  const createProduct = useMutation({
    mutationFn: async (product: Omit<Product, 'id' | 'organizationId' | 'provider'>) => {
      if (!currentOrganization) throw new Error('No organization selected');
      return productsService.createProduct(product, currentOrganization.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
    },
    onError: (error: any) => { console.error('Error creating product:', error)}
  });

  const updateProduct = useMutation({
    mutationFn: async (product: Product) => {
      return productsService.updateProduct(product);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.promotions(orgId) });
    },
  });

  const deleteProduct = useMutation({
    mutationFn: async (productId: string) => {
      return productsService.deleteProduct(productId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productIngredientsCatalog(orgId) });
      sonnerToast.success(translator('Products.successDelete'), {
        description: translator('Products.successDeleteDescription'),
        duration: 5000,
      });
    },
    onError: (error: any) => {
      if (error.code === '23503') {
        sonnerToast.error(translator('Products.productCannotBeRemoved'), {
          description: translator('Products.productHasAlreadyBeenSold'),
          duration: 5000,
        });
      } else {
        sonnerToast.error(translator('Errors.unexpectedError'), {
          description: error.message,
          duration: 5000,
        });
      }
    },
  });

  /** Deletes without success toast — for transactional rollback (e.g. failed promo create). */
  const deleteProductQuiet = useMutation({
    mutationFn: async (productId: string) => productsService.deleteProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
    },
  });

  const pinProduct = useMutation({
    mutationFn: async ({ productId, isPinned }: { productId: string; isPinned: boolean }) => {
      return productsService.togglePinProduct(productId, isPinned);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
    },
  });

  const adjustStock = useMutation({
    mutationFn: async ({ productId, delta }: { productId: string; delta: number }) => {
      return productsService.updateProductStockByDelta(productId, delta);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.productsForSales(orgId) });
    },
    onError: (error: Error & { code?: string }) => {
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: error.message,
        duration: 5000,
      });
    },
  });

  return {
    products,
    productsForSales,
    isLoading,
    isLoadingForSales,
    error,
    deleteError: deleteProduct.error,
    createProduct: createProduct.mutate,
    createProductAsync: createProduct.mutateAsync,
    updateProduct: updateProduct.mutate,
    deleteProduct: deleteProduct.mutate,
    updateProductAsync: updateProduct.mutateAsync,
    deleteProductAsync: deleteProduct.mutateAsync,
    deleteProductQuietAsync: deleteProductQuiet.mutateAsync,
    pinProduct: pinProduct.mutate,
    adjustStock: adjustStock.mutate,
    isCreating: createProduct.isPending,
    isUpdating: updateProduct.isPending,
    isDeleting: deleteProduct.isPending,
  };
};
